import { getStrapiToken } from '@/services/auth/getStrapiToken';
// @/services/app/store/modules/supplier-engagement-batch-remind.js

import { trackCustomEvent } from '@/utils/tracking';

/**
 * Server-driven batch reminder.
 *
 * Frontend POSTs the selected linkingIds to /supplier-engagement/.../batch-remind.
 * The backend persists a job record, a cron worker reminds suppliers ONE AT A
 * TIME with a fixed delay between calls (so a large lead-firm selection can
 * never overload the shared Snowflake warehouse), and streams progress via
 * WebSocket (batch-remind:progress / :completed / :failed / :cancelled).
 *
 * Why server-driven: navigate-away + reload + re-login resumability. The banner
 * hydrates from /batch-remind/active on mount, so an in-flight batch is always
 * visible until the user dismisses it.
 *
 * State shape (store.state.data.supplierEngagementBatchRemind):
 *   {
 *     activeBatches: [{
 *       batchId, status: 'queued'|'processing'|'completed'|'failed'|'cancelled',
 *       totalRows, processedRows, successCount, errorCount, errorMessage?
 *     }],
 *     dismissedBatchIds: Set<number>
 *   }
 */

const STATE_KEY = 'supplierEngagementBatchRemind';

export class SupplierEngagementBatchRemindHandler {
  constructor(store) {
    this.store = store;
    this.registered = false;

    if (!store.state.data[STATE_KEY]) {
      store.state.data[STATE_KEY] = {
        activeBatches: [],
        dismissedBatchIds: new Set()
      };
    }
  }

  registerWebSocketHandlers() {
    if (this.registered) return;
    const ws = this.store.webSocketHandler;
    if (!ws?.registerHandler) return;

    ws.registerHandler(
      'seBatchRemindProgress',
      (event) => this._applyEvent(event),
      [
        'batch-remind:progress',
        'batch-remind:completed',
        'batch-remind:failed',
        'batch-remind:cancelled'
      ]
    );

    this.registered = true;
  }

  _applyEvent(event) {
    const type = event?.type || event?.event;
    const data = event?.data || event;
    if (!data?.batchId) return;

    const current = this.store.state.data[STATE_KEY];
    if (current.dismissedBatchIds.has(data.batchId)) return;

    const existing = current.activeBatches.find(b => b.batchId === data.batchId);
    const others = current.activeBatches.filter(b => b.batchId !== data.batchId);

    if (type === 'batch-remind:progress') {
      this._updateState({
        activeBatches: [
          ...others,
          { ...(existing || {}), ...data, status: 'processing' }
        ],
        dismissedBatchIds: current.dismissedBatchIds
      });
      return;
    }

    if (type === 'batch-remind:completed' || type === 'batch-remind:cancelled') {
      const status = type === 'batch-remind:cancelled' ? 'cancelled' : 'completed';
      const totalRows = data.totalRows ?? existing?.totalRows;
      this._updateState({
        activeBatches: [
          ...others,
          {
            ...(existing || {}),
            batchId: data.batchId,
            status,
            totalRows,
            processedRows: data.processedRows ?? existing?.processedRows ?? totalRows,
            successCount: data.successCount ?? existing?.successCount ?? 0,
            errorCount: data.errorCount ?? existing?.errorCount ?? 0,
            skippedCount: data.skippedCount ?? existing?.skippedCount ?? 0,
            errorReasons: data.errorReasons ?? existing?.errorReasons ?? {}
          }
        ],
        dismissedBatchIds: current.dismissedBatchIds
      });

      // Reminders bump LastLinkingRequest / ReminderCount, so refresh the list
      // once on terminal state to reflect the new "last reminded" timestamps.
      this.store.supplychainSuppliersHandler?.loadSupplychainSuppliers?.(true);

      trackCustomEvent('supplier_batch_remind_complete', {
        batch_id: data.batchId,
        supplier_count: totalRows,
        success_count: data.successCount ?? 0,
        error_count: data.errorCount ?? 0,
        cancelled: status === 'cancelled'
      });
      return;
    }

    if (type === 'batch-remind:failed') {
      this._updateState({
        activeBatches: [
          ...others,
          {
            ...(existing || {}),
            batchId: data.batchId,
            status: 'failed',
            errorMessage: data.error || 'Batch failed'
          }
        ],
        dismissedBatchIds: current.dismissedBatchIds
      });
    }
  }

  _updateState(next) {
    this.store.state.data[STATE_KEY] = next;
    this.store.notifySubscribers?.(STATE_KEY, next);
  }

  /**
   * Enqueue a reminder batch on the backend. Returns as soon as the job record
   * is created — the cron worker drives the actual reminders, reported via WS.
   */
  async submitBatch({ linkingIds, templateId = null }) {
    const projectId = this.store.getProjectId?.();
    if (!projectId) throw new Error('No project ID available');
    if (!Array.isArray(linkingIds) || linkingIds.length === 0) {
      throw new Error('No suppliers to remind');
    }

    const response = await fetch(
      `${process.env.GATSBY_STRAPI_URL}/api/supplier-engagement/project/${projectId}/batch-remind`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getStrapiToken()}`
        },
        body: JSON.stringify({ linkingIds, templateId })
      }
    );

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error?.message || err.message || `Batch enqueue failed (${response.status})`);
    }

    const body = await response.json();
    const current = this.store.state.data[STATE_KEY];
    this._updateState({
      activeBatches: [
        ...current.activeBatches,
        {
          batchId: body.batchId,
          status: body.status || 'queued',
          totalRows: body.totalRows,
          processedRows: 0,
          successCount: 0,
          errorCount: 0
        }
      ],
      dismissedBatchIds: current.dismissedBatchIds
    });

    return body;
  }

  /**
   * Hydrate the banner on page mount with any queued/processing batches, so an
   * in-flight job shows up after reload / re-login / navigate back.
   */
  async fetchActive() {
    const projectId = this.store.getProjectId?.();
    if (!projectId) return [];

    const response = await fetch(
      `${process.env.GATSBY_STRAPI_URL}/api/supplier-engagement/project/${projectId}/batch-remind/active`,
      {
        headers: { Authorization: `Bearer ${getStrapiToken()}` }
      }
    );

    if (!response.ok) return [];
    const batches = await response.json();

    const current = this.store.state.data[STATE_KEY];
    const notDismissed = Array.isArray(batches)
      ? batches.filter(b => !current.dismissedBatchIds.has(b.batchId))
      : [];

    this._updateState({
      activeBatches: notDismissed,
      dismissedBatchIds: current.dismissedBatchIds
    });

    return batches;
  }

  async fetchBatch(batchId) {
    const projectId = this.store.getProjectId?.();
    if (!projectId || !batchId) return null;

    const response = await fetch(
      `${process.env.GATSBY_STRAPI_URL}/api/supplier-engagement/project/${projectId}/batch-remind/${batchId}`,
      {
        headers: { Authorization: `Bearer ${getStrapiToken()}` }
      }
    );
    if (!response.ok) return null;
    return response.json();
  }

  /**
   * Ask the backend to stop an in-flight batch. The worker stops at its next
   * checkpoint; already-sent reminders are not undone. The terminal
   * 'batch-remind:cancelled' WS event updates the banner.
   */
  async cancelBatch(batchId) {
    const projectId = this.store.getProjectId?.();
    if (!projectId || !batchId) return null;

    const response = await fetch(
      `${process.env.GATSBY_STRAPI_URL}/api/supplier-engagement/project/${projectId}/batch-remind/${batchId}/cancel`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${getStrapiToken()}` }
      }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error?.message || err.message || `Cancel failed (${response.status})`);
    }
    return response.json();
  }

  dismissBatch(batchId) {
    const current = this.store.state.data[STATE_KEY];
    const dismissed = new Set(current.dismissedBatchIds);
    dismissed.add(batchId);

    this._updateState({
      activeBatches: current.activeBatches.filter(b => b.batchId !== batchId),
      dismissedBatchIds: dismissed
    });
  }
}

export const SUPPLIER_ENGAGEMENT_BATCH_REMIND_STATE_KEY = STATE_KEY;
